import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CommandPalette } from './CommandPalette';
import { Footer } from './Footer';
import { Header } from './Header';
import { MobileNav } from './MobileNav';

/* ────────────────────────────────────────────────────────────────────────────
   Command palette plumbing.

   The palette is owned by the shell so that the header search pill, the mobile
   "Search" tab and the global ⌘K listener all drive one instance. It is a tiny
   context rather than a store because nothing outside the shell needs it.
   ──────────────────────────────────────────────────────────────────────────── */

export interface CommandPaletteApi {
  open: boolean;
  openPalette: () => void;
  closePalette: () => void;
  togglePalette: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteApi | null>(null);

export function useCommandPalette(): CommandPaletteApi {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) throw new Error('useCommandPalette() must be used inside <AppShell>.');
  return ctx;
}

/* ────────────────────────────────────────────────────────────────────────────
   Wallet-connect plumbing.

   Same shape as the palette above, for the same reason: the connect modal has to
   be reachable from anywhere — the header button, an empty portfolio, the trade
   panel's primary CTA — so its open state lives on the shell and the modal
   itself is rendered exactly once, inside `<WalletButton>`.
   ──────────────────────────────────────────────────────────────────────────── */

export interface ConnectModalApi {
  open: boolean;
  setOpen: (v: boolean) => void;
  openModal: () => void;
  close: () => void;
}

const ConnectModalContext = createContext<ConnectModalApi | null>(null);

export function useConnectModal(): ConnectModalApi {
  const ctx = useContext(ConnectModalContext);
  if (!ctx) throw new Error('useConnectModal() must be used inside <AppShell>.');
  return ctx;
}

export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);
  const togglePalette = useCallback(() => setOpen((v) => !v), []);

  const openModal = useCallback(() => setConnectOpen(true), []);
  const closeModal = useCallback(() => setConnectOpen(false), []);

  // Global ⌘K / Ctrl+K. Also accepts ⌘/ as a courtesy shortcut.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key !== 'k' && key !== '/') return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const paletteApi = useMemo<CommandPaletteApi>(
    () => ({ open, openPalette, closePalette, togglePalette }),
    [open, openPalette, closePalette, togglePalette],
  );

  const connectApi = useMemo<ConnectModalApi>(
    () => ({ open: connectOpen, setOpen: setConnectOpen, openModal, close: closeModal }),
    [connectOpen, openModal, closeModal],
  );

  return (
    <CommandPaletteContext.Provider value={paletteApi}>
      <ConnectModalContext.Provider value={connectApi}>
        <div className="flex min-h-screen flex-col bg-canvas">
          {/* Decorative backdrop: bull-tinted glow over an engineering grid that
              fades into the page. Sits behind every layer and eats no clicks. */}
          <div
            aria-hidden="true"
            className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[520px] overflow-hidden"
          >
            <div className="absolute inset-0 bg-grid-fade" />
            <div className="texture-grid absolute inset-0 opacity-70 [-webkit-mask-image:linear-gradient(to_bottom,rgb(0_0_0)_0%,transparent_88%)] [mask-image:linear-gradient(to_bottom,rgb(0_0_0)_0%,transparent_88%)]" />
          </div>

          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:border focus:border-line-strong focus:bg-overlay focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-fg focus:shadow-pop"
          >
            Skip to content
          </a>

          <Header />

          {/* pb-24 keeps the mobile tab bar from ever covering page content. */}
          <main id="main" className="flex-1 pb-24 lg:pb-0">
            {children}
          </main>

          <Footer />
          <MobileNav />
          <CommandPalette />
        </div>
      </ConnectModalContext.Provider>
    </CommandPaletteContext.Provider>
  );
}

export default AppShell;
